import { nextTick } from 'vue'
import { useNuxtApp, useRouter } from 'nuxt/app'
import { useLogger } from '@common/core-layer/composables/useLogger'

import { AnalyticsEvents } from '../types'
import { analyticsService } from '../services/analytics.service'
import { resolveAnalyticsContext, resolveDefaultAnalyticsContext } from './analytics-context'

import type { ResolvedAnalyticsContext } from './analytics-context'
import type { PendoAnalyticsProvider } from '../providers/pendo-analytics.provider'
import type { RouteLocationNormalized } from 'vue-router'

export interface PageViewTrackingOptions {
  /** Gates the analyticsService PAGE_VIEW (GA/backend). Does NOT gate pendo. */
  gate: () => boolean
  /** Ungated product page-hit provider; fires after context resolves. */
  pendo?: PendoAnalyticsProvider | null
}

/** Prefer the live document title; fall back to a title-cased route name, then the raw path. */
function resolvePageTitle(to: RouteLocationNormalized): string {
  try {
    const domTitle = document.title
    if (domTitle && domTitle.trim() !== '') return domTitle
    if (!to.name) return to.path
    return String(to.name)
      .split('-')
      .filter(part => !part.includes('Slug') && !part.includes('['))
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  } catch {
    return to.path
  }
}

/** RouteLocationNormalized.name is string | symbol | null | undefined — normalise to a string or drop it. */
function routeNameOf(route?: RouteLocationNormalized): string | undefined {
  return route?.name ? String(route.name) : undefined
}

/**
 * Single page-view pipeline for all providers. Each navigation awaits the analytics context
 * resolver (tenant locationId on gokollab; immediate elsewhere), then dispatches once with the
 * settled context. navToken guarantees one dispatch per navigation — superseded navigations are
 * dropped, never fired with stale context.
 */
export function startPageViewTracking(options: PageViewTrackingOptions): () => void {
  const router = useRouter()
  const nuxtApp = useNuxtApp()
  const { logger } = useLogger()

  // Every navigation gets an incrementing id; only the latest may dispatch, so a slow resolve that
  // finishes after a newer navigation is dropped. `inFlightResolve` is the AbortController for the
  // navigation currently being resolved — aborting it cancels the previous resolve (freeing its
  // store watcher and timeout) the moment a newer navigation starts, or when the pipeline is torn down.
  let latestNavId = 0
  let inFlightResolve: AbortController | null = null
  let destroyed = false

  const handleNavigation = async (to: RouteLocationNormalized, from?: RouteLocationNormalized) => {
    if (destroyed) return
    const navId = ++latestNavId
    inFlightResolve?.abort()
    const abortController = new AbortController()
    inFlightResolve = abortController

    let resolved: ResolvedAnalyticsContext
    try {
      resolved = await resolveAnalyticsContext(to, { signal: abortController.signal })
    } catch (error: unknown) {
      if (abortController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return

      logger.error('[Analytics] Context resolver failed', {
        error: new Error('Analytics context resolver failed'),
        payload: { routeName: routeNameOf(to) },
      })
      const fallback = resolveDefaultAnalyticsContext()
      // Dedicated, controlled-payload event — NOT the generic ERROR_OCCURRED (see backend provider map).
      analyticsService.trackProviderIsolated('backendAnalytics', AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR, {
        errorMessage: 'Analytics context resolver failed',
        errorType: 'analytics_context_resolver',
        routeName: routeNameOf(to),
        locationId: fallback.locationId || 'NA',
        contactId: 'NA',
      })
      // Keep the default identity's platform contactId (do not null it) — the isolated error event
      // above is the only thing that must stay identity-free.
      resolved = { ...fallback, tenantContextStatus: 'fallback_error' }
    }
    if (navId !== latestNavId) return

    // Route template (e.g. /courses/:productId/lessons) — groups all parameterised
    // variations into one funnel step.
    const slugifiedRoute = to.matched.at(-1)?.path ?? to.path
    const fromSlugifiedRoute = from?.matched.at(-1)?.path ?? from?.path
    const activeTab = Array.isArray(to.query?.activeTab) ? to.query.activeTab[0] : to.query?.activeTab

    // Built once, shared by the service context and the pendo hit so the two sinks can never desync.
    const pageContext = {
      pagePath: to.path,
      routeName: routeNameOf(to),
      slugifiedRoute,
      locationId: resolved.locationId,
      tenantContextStatus: resolved.tenantContextStatus,
    }
    // Identity fields are conditional: never clobber a previously-known contactId with undefined
    // (a logged-in session with no contactId on this navigation must keep its prior identity).
    const identity = {
      ...(resolved.contactId ? { contactId: resolved.contactId } : {}),
      ...(resolved.gokollabContactId ? { gokollabContactId: resolved.gokollabContactId } : {}),
    }

    analyticsService.setContext({ ...pageContext, ...identity })

    // Pendo: ungated (logged-out + first-load hits); guards on locationId itself.
    options.pendo?.track(AnalyticsEvents.PAGE_VIEW, { ...pageContext, ...identity, activeTab: activeTab || undefined })

    nextTick(() => {
      if (navId !== latestNavId || !options.gate()) return

      analyticsService.track(AnalyticsEvents.PAGE_VIEW, {
        pageLocation: window.location.href,
        pageTitle: resolvePageTitle(to),
        route: to.path,
        slugifiedRoute,
        activeTab: activeTab || undefined,
        fromPagePath: from?.path,
        fromRouteName: routeNameOf(from),
        fromSlugifiedRoute,
        fromPageLocation: from?.fullPath ? window.location.origin + from.fullPath : undefined,
      })
    })
  }

  const removeAfterEach = router.afterEach((to, from) => void handleNavigation(to, from))

  // Nuxt's afterEach does NOT fire for the initial route, so dispatch it once on app:mounted. That
  // hook runs after ALL plugins (layer + app) have set up, so an app-registered tenant resolver
  // (e.g. gokollab's) is guaranteed present before this first resolve — no layer-vs-app ordering
  // race. Skipped if a real navigation already fired (latestNavId advanced) or the pipeline was
  // torn down before mount.
  const removeMountedHook = nuxtApp.hooks.hookOnce('app:mounted', () => {
    if (!destroyed && latestNavId === 0) void handleNavigation(router.currentRoute.value)
  })

  return () => {
    destroyed = true
    inFlightResolve?.abort()
    removeAfterEach()
    removeMountedHook()
  }
}
